import {
  getAuthSnapshot,
  onAuthStateChange,
  signInWithGoogle,
  signInWithMagicLink,
  signOut,
  type AuthSnapshot,
} from "../lib/auth";
import { parseAndValidateCsv } from "../lib/csv";
import {
  assumedConsentMethod,
  consentMethods,
  csvHeaders,
  usagePlans,
} from "../lib/defaults";
import {
  loadUploadDetails,
  loadAppData,
  markUploadReady,
  saveBusinessProfile,
  saveValidatedUpload,
} from "../lib/data";
import type {
  AppData,
  BusinessAccount,
  CsvValidationResult,
  UploadDetails,
  UploadRecord,
  UploadRow,
  UsagePlanId,
} from "../lib/types";

type RouteName =
  | "dashboard"
  | "profile"
  | "upload"
  | "billing";

const routes: Array<{ name: RouteName; label: string; path: string }> = [
  { name: "dashboard", label: "Dashboard", path: "/" },
  { name: "profile", label: "Profile", path: "/profile" },
  { name: "upload", label: "Upload", path: "/upload" },
  { name: "billing", label: "Billing", path: "/billing" },
];

export function createApp(root: HTMLDivElement | null) {
  if (!root) {
    throw new Error("Application root not found.");
  }

  const mountNode = root;

  const state = {
    auth: {
      configured: false,
      session: null,
      user: null,
    } as AuthSnapshot,
    appData: null as AppData | null,
    route: resolveRoute(window.location.pathname),
    busy: false,
    loadingData: false,
    notice: "",
    error: "",
    csvResult: null as CsvValidationResult | null,
    csvFileName: "",
    lastSavedUploadId: "",
    selectedUploadDetails: null as UploadDetails | null,
  };

  function setNotice(message = "") {
    state.notice = message;
  }

  function setError(message = "") {
    state.error = message;
  }

  function setBusy(value: boolean) {
    state.busy = value;
  }

  function navigate(path: string) {
    window.history.pushState({}, "", path);
    state.route = resolveRoute(path);
    render();
  }

  window.addEventListener("popstate", () => {
    state.route = resolveRoute(window.location.pathname);
    render();
  });

  async function refreshAppData() {
    if (!state.auth.user) {
      state.appData = null;
      return;
    }

    state.loadingData = true;
    render();

    try {
      state.appData = await loadAppData(
        state.auth.user.id,
        state.auth.user.email ?? null,
      );
    } catch (error) {
      setError(readError(error));
    } finally {
      state.loadingData = false;
      render();
    }
  }

  async function handleGoogleSignIn() {
    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      await signInWithGoogle();
    } catch (error) {
      setError(readError(error));
      setBusy(false);
      render();
    }
  }

  async function handleMagicLink(event: SubmitEvent) {
    event.preventDefault();
    setBusy(true);
    setError();
    setNotice();
    render();

    const form = event.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const email = String(formData.get("email") ?? "").trim();

    if (!email) {
      setError("Enter an email address.");
      setBusy(false);
      render();
      return;
    }

    try {
      await signInWithMagicLink(email);
      form.reset();
      setNotice("Magic link sent. Check your email to finish signing in.");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleSignOut() {
    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      await signOut();
      state.appData = null;
      state.csvResult = null;
      state.csvFileName = "";
      state.lastSavedUploadId = "";
      state.selectedUploadDetails = null;
      navigate("/");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleProfileSave(event: SubmitEvent) {
    event.preventDefault();
    if (!state.appData?.account) {
      return;
    }

    setBusy(true);
    setError();
    setNotice();
    render();

    const form = event.currentTarget as HTMLFormElement;
    const formData = new FormData(form);

    try {
      const account = await saveBusinessProfile(state.appData.account.id, {
        business_name: String(formData.get("business_name") ?? "").trim(),
        business_address: String(formData.get("business_address") ?? "").trim(),
        business_phone: String(formData.get("business_phone") ?? "").trim(),
        contact_name: String(formData.get("contact_name") ?? "").trim(),
        primary_email: String(formData.get("primary_email") ?? "").trim(),
        google_review_url: String(formData.get("google_review_url") ?? "").trim(),
        facebook_review_url: String(formData.get("facebook_review_url") ?? "").trim(),
        usage_plan_id: usagePlanIdFromForm(formData.get("usage_plan_id")),
        resend_suppression_days: nonNegativeIntegerFromForm(
          formData.get("resend_suppression_days"),
          state.appData.account.resend_suppression_days,
        ),
      });

      state.appData.account = account;
      setNotice("Business profile saved.");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleCsvSelected(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    setBusy(true);
    setError();
    setNotice();
    state.csvResult = null;
    state.csvFileName = file.name;
    render();

    try {
      const content = await file.text();
      const validation = parseAndValidateCsv(content);
      state.csvResult = validation;

      if (!state.appData?.account) {
        throw new Error("Business account is not available.");
      }

      const savedUpload = await saveValidatedUpload(
        state.appData.account.id,
        state.auth.user?.id ?? "",
        file,
        validation,
      );

      state.lastSavedUploadId = savedUpload.upload.id;
      state.selectedUploadDetails = savedUpload;
      await refreshAppData();
      setNotice("CSV validated, stored in Firebase Storage, and saved to Firestore.");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleSelectUpload(event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    const uploadId = button.dataset.uploadId;
    if (!state.appData?.account || !uploadId) {
      return;
    }

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      state.selectedUploadDetails = await loadUploadDetails(state.appData.account.id, uploadId);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleStartUpload(event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    const uploadId = button.dataset.uploadId;
    if (!state.appData?.account || !uploadId) {
      return;
    }

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      state.selectedUploadDetails = await markUploadReady(state.appData.account.id, uploadId);
      await refreshAppData();
      setNotice("Upload marked ready. Valid rows are queued and resend suppression has been applied.");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  function bindEvents() {
    mountNode.querySelectorAll<HTMLAnchorElement>("[data-nav]").forEach((node) => {
      node.addEventListener("click", (event) => {
        event.preventDefault();
        navigate(node.getAttribute("href") || "/");
      });
    });

    mountNode
      .querySelector<HTMLButtonElement>("[data-google-login]")
      ?.addEventListener("click", handleGoogleSignIn);

    mountNode
      .querySelector<HTMLFormElement>("[data-magic-link-form]")
      ?.addEventListener("submit", handleMagicLink);

    mountNode
      .querySelector<HTMLButtonElement>("[data-sign-out]")
      ?.addEventListener("click", handleSignOut);

    mountNode
      .querySelector<HTMLFormElement>("[data-profile-form]")
      ?.addEventListener("submit", handleProfileSave);

    mountNode
      .querySelector<HTMLInputElement>("[data-csv-input]")
      ?.addEventListener("change", handleCsvSelected);

    mountNode.querySelectorAll<HTMLButtonElement>("[data-select-upload]").forEach((node) => {
      node.addEventListener("click", handleSelectUpload);
    });

    mountNode.querySelectorAll<HTMLButtonElement>("[data-start-upload]").forEach((node) => {
      node.addEventListener("click", handleStartUpload);
    });
  }

  function render() {
    mountNode.innerHTML = state.auth.user
      ? renderAuthedApp(state)
      : renderAuthLanding(state);
    bindEvents();
  }

  getAuthSnapshot()
    .then(async (snapshot) => {
      state.auth = snapshot;
      render();
      if (snapshot.user) {
        await refreshAppData();
      }
    })
    .catch((error) => {
      setError(readError(error));
      render();
    });

  onAuthStateChange(async (snapshot) => {
    state.auth = snapshot;
    render();
    if (snapshot.user) {
      await refreshAppData();
    }
  });

  render();
}

function resolveRoute(pathname: string): RouteName {
  const matched = routes.find((route) => route.path === pathname);
  return matched?.name ?? "dashboard";
}

function renderAuthLanding(state: {
  auth: AuthSnapshot;
  busy: boolean;
  notice: string;
  error: string;
}) {
  return `
    <main class="auth-shell">
      <section class="hero-card">
        <div class="eyebrow">Rate Follow Up</div>
        <h1>Business login</h1>
        <p class="lede">
          Sign in to manage review follow-ups, uploads, and quota tracking.
        </p>
        ${renderBanner(state)}
        ${state.auth.configured ? renderLoginPanel(state) : renderConfigPanel()}
      </section>
    </main>
  `;
}

function renderLoginPanel(state: { busy: boolean }) {
  return `
    <div class="stack">
      <button class="primary-button" data-google-login ${disabledAttr(state.busy)}>
        Continue with Google
      </button>
      <form class="magic-link-form" data-magic-link-form>
        <label for="email">Email link</label>
        <div class="inline-form">
          <input
            id="email"
            name="email"
            type="email"
            placeholder="owner@business.com"
            ${disabledAttr(state.busy)}
            required
          />
          <button class="secondary-button" type="submit" ${disabledAttr(state.busy)}>
            Send link
          </button>
        </div>
      </form>
    </div>
  `;
}

function renderConfigPanel() {
  return `
    <div class="warning-card">
      <h2>Firebase configuration required</h2>
      <p>
        Add the <code>VITE_FIREBASE_*</code> environment variables
        before deploying or running the app.
      </p>
    </div>
  `;
}

function renderAuthedApp(state: {
  auth: AuthSnapshot;
  appData: AppData | null;
  route: RouteName;
  busy: boolean;
  loadingData: boolean;
  notice: string;
  error: string;
  csvResult: CsvValidationResult | null;
  csvFileName: string;
  lastSavedUploadId: string;
  selectedUploadDetails: UploadDetails | null;
}) {
  const activeRoute = routes.find((route) => route.name === state.route) ?? routes[0];
  const profileComplete = Boolean(state.appData?.account?.is_profile_complete);

  return `
    <div class="app-shell">
      <aside class="sidebar">
        <div>
          <div class="eyebrow">Rate Follow Up</div>
          <h1 class="sidebar-title">Operations</h1>
        </div>
        <nav class="nav-list">
          ${routes
            .map(
              (route) => `
                <a
                  href="${route.path}"
                  data-nav
                  class="nav-link ${route.name === state.route ? "active" : ""}"
                >
                  ${route.label}
                </a>
              `,
            )
            .join("")}
        </nav>
        <div class="sidebar-footer">
          <div class="account-label">${escapeHtml(state.auth.user?.email ?? "")}</div>
          <button class="secondary-button" data-sign-out ${disabledAttr(state.busy)}>
            Sign out
          </button>
        </div>
      </aside>
      <main class="content-shell">
        <header class="content-header">
          <div>
            <div class="eyebrow">App</div>
            <h2>${activeRoute.label}</h2>
          </div>
          ${profileComplete ? '<span class="status-pill ready">Profile complete</span>' : '<span class="status-pill pending">Profile required</span>'}
        </header>
        ${renderBanner(state)}
        ${state.loadingData ? renderLoadingPanel() : renderRouteContent(state)}
      </main>
    </div>
  `;
}

function renderRouteContent(state: {
  appData: AppData | null;
  route: RouteName;
  csvResult: CsvValidationResult | null;
  csvFileName: string;
  lastSavedUploadId: string;
  selectedUploadDetails: UploadDetails | null;
}) {
  switch (state.route) {
    case "profile":
      return renderProfilePage(state.appData);
    case "upload":
      return renderUploadPage(
        state.appData,
        state.csvResult,
        state.csvFileName,
        state.lastSavedUploadId,
        state.selectedUploadDetails,
      );
    case "billing":
      return renderBillingPage(state.appData);
    case "dashboard":
    default:
      return renderDashboardPage(state.appData);
  }
}

function renderDashboardPage(appData: AppData | null) {
  const account = appData?.account;
  const uploads = appData?.uploads ?? [];
  const reviewStatus = account?.google_review_url || account?.facebook_review_url ? "Configured" : "Missing";
  const selectedPlan = getUsagePlan(account?.usage_plan_id);

  return `
    <section class="grid">
      ${renderMetric("Plan", `${selectedPlan.label} - $${selectedPlan.monthlyPrice} / month`)}
      ${renderMetric("Included messages", formatNumber(selectedPlan.includedMessages))}
      ${renderMetric("Review links", reviewStatus)}
      ${renderMetric("Uploads tracked", String(uploads.length))}
    </section>
    <section class="panel stack-panel">
      <h3>Account readiness</h3>
      <ul class="check-list">
        <li>${account?.business_name ? "Business name saved" : "Business name still needed"}</li>
        <li>${account?.business_address ? "Business address saved" : "Business address still needed"}</li>
        <li>${account?.business_phone ? "Business phone saved" : "Business phone still needed"}</li>
        <li>${account?.contact_name ? "Signup contact name saved" : "Signup contact name still needed"}</li>
        <li>${account?.primary_email ? "Primary contact email saved" : "Primary contact email still needed"}</li>
        <li>${account?.usage_plan_id ? "Usage level selected" : "Usage level still needed"}</li>
        <li>${reviewStatus === "Configured" ? "At least one review URL is configured" : "At least one review URL is required"}</li>
        <li>Compliance message copy is fixed for all accounts</li>
      </ul>
    </section>
    <section class="panel stack-panel">
      <h3>Recent upload records</h3>
      ${uploads.length ? renderUploadsTable(uploads) : "<p>No upload records yet.</p>"}
    </section>
    <section class="panel stack-panel">
      <h3>Recent audit events</h3>
      ${
        appData?.auditLogs.length
          ? `
            <div class="log-list">
              ${appData.auditLogs
                .map(
                  (log) => `
                    <div class="log-item">
                      <div class="log-title">${escapeHtml(log.event_type)}</div>
                      <div class="log-meta">${formatDate(log.created_at)}</div>
                    </div>
                  `,
                )
                .join("")}
            </div>
          `
          : "<p>No audit entries yet.</p>"
      }
    </section>
  `;
}

function renderProfilePage(appData: AppData | null) {
  const account = appData?.account;

  return `
    <section class="panel">
      <h3>Business profile</h3>
      <p>
        Uploads are blocked until contact details, a usage level, and at least one review URL are saved.
      </p>
      <form class="stack-panel" data-profile-form>
        <label class="field">
          <span>Business name</span>
          <input
            name="business_name"
            value="${escapeHtml(account?.business_name ?? "")}"
            required
          />
        </label>
        <label class="field">
          <span>Business address</span>
          <textarea
            name="business_address"
            rows="3"
            required
          >${escapeHtml(account?.business_address ?? "")}</textarea>
        </label>
        <label class="field">
          <span>Business phone</span>
          <input
            name="business_phone"
            type="tel"
            value="${escapeHtml(account?.business_phone ?? "")}"
            required
          />
        </label>
        <label class="field">
          <span>Name of person signing up</span>
          <input
            name="contact_name"
            value="${escapeHtml(account?.contact_name ?? "")}"
            required
          />
        </label>
        <label class="field">
          <span>Email of person signing up</span>
          <input
            name="primary_email"
            type="email"
            value="${escapeHtml(account?.primary_email ?? "")}"
            required
          />
        </label>
        <label class="field">
          <span>Level of use</span>
          <select name="usage_plan_id" required>
            ${renderUsagePlanOptions(account)}
          </select>
        </label>
        <label class="field">
          <span>Resend suppression window</span>
          <input
            name="resend_suppression_days"
            type="number"
            min="0"
            step="1"
            value="${String(account?.resend_suppression_days ?? 90)}"
            required
          />
          <small class="field-help">
            Do not queue the same number again if it was contacted within this many days.
            Enter 0 to never resend to a number after it has been queued once.
          </small>
        </label>
        <label class="field">
          <span>Google review URL</span>
          <input
            name="google_review_url"
            type="url"
            placeholder="https://g.page/r/..."
            value="${escapeHtml(account?.google_review_url ?? "")}"
          />
        </label>
        <details class="help-disclosure">
          <summary>How to get this from Google Business Profile</summary>
          <div class="help-disclosure-body">
            <ol>
              <li>Open your Google Business Profile at <a href="https://business.google.com/" target="_blank" rel="noreferrer">business.google.com</a>.</li>
              <li>Select <strong>Read Reviews</strong>, then choose <strong>Get more reviews</strong>.</li>
              <li>Select <strong>Copy</strong> to copy the review request link.</li>
              <li>Paste that copied link into the Google review URL field above.</li>
            </ol>
            <p class="muted">
              Google says review links should only be sent to customers with a genuine experience. Do not offer discounts, free items, or other incentives for reviews.
            </p>
          </div>
        </details>
        <label class="field">
          <span>Facebook review URL</span>
          <input
            name="facebook_review_url"
            type="url"
            placeholder="https://www.facebook.com/..."
            value="${escapeHtml(account?.facebook_review_url ?? "")}"
          />
          <small class="field-help">
            Facebook review links are typically formatted like
            <code>https://facebook.com/YOURPAGENAME/reviews</code>.
          </small>
        </label>
        <button class="primary-button" type="submit">Save profile</button>
      </form>
    </section>
  `;
}

function renderUploadPage(
  appData: AppData | null,
  csvResult: CsvValidationResult | null,
  csvFileName: string,
  lastSavedUploadId: string,
  selectedUploadDetails: UploadDetails | null,
) {
  const account = appData?.account;
  const profileComplete = Boolean(account?.is_profile_complete);
  const uploads = appData?.uploads ?? [];

  return `
    <section class="panel stack-panel">
      <h3>CSV upload contract</h3>
      <p>
        Upload processing is blocked until the business profile is complete.
        The current v1 contract requires exactly two headers in this order.
      </p>
      <div class="code-chip-row">
        ${csvHeaders.map((header) => `<span class="code-chip">${header}</span>`).join("")}
      </div>
      <p>
        Consent timestamp is assumed at upload time. Consent method is stored as
        <code>${assumedConsentMethod}</code>. Available consent method values are:
        ${consentMethods.join(", ")}.
      </p>
      <label class="upload-drop ${profileComplete ? "" : "disabled"}">
        <span>Select CSV file</span>
        <input type="file" accept=".csv,text/csv" data-csv-input ${disabledAttr(!profileComplete)} />
      </label>
      ${!profileComplete ? "<p class=\"muted\">Complete the profile page before upload is enabled.</p>" : ""}
    </section>
    <section class="panel stack-panel">
      <h3>Saved upload batches</h3>
      <p>
        Review a saved batch, then start sending when the rows are correct.
        Starting applies the resend suppression window before rows are queued.
      </p>
      ${uploads.length ? renderUploadBatchTable(uploads, selectedUploadDetails?.upload.id ?? lastSavedUploadId) : "<p>No upload records yet.</p>"}
    </section>
    ${
      csvResult
        ? `
          <section class="panel stack-panel">
            <h3>Validation results ${csvFileName ? `for ${escapeHtml(csvFileName)}` : ""}</h3>
            ${lastSavedUploadId ? `<p class="muted">Saved upload record: ${escapeHtml(lastSavedUploadId)}</p>` : ""}
            <section class="grid compact-grid">
              ${renderMetric("Rows read", String(csvResult.summary.totalRows))}
              ${renderMetric("Valid", String(csvResult.summary.validRows))}
              ${renderMetric("Rejected", String(csvResult.summary.rejectedRows))}
            </section>
            ${renderValidationTable(csvResult)}
          </section>
        `
        : ""
    }
    ${selectedUploadDetails ? renderSelectedUploadDetails(selectedUploadDetails) : ""}
  `;
}

function renderBillingPage(appData?: AppData | null) {
  const account = appData?.account;
  const selectedPlan = getUsagePlan(account?.usage_plan_id);

  return `
    <section class="panel stack-panel">
      <h3>Billing</h3>
      <p>
        Square billing is not wired yet. The selected usage level is stored in
        Firebase and will be used when checkout is connected.
      </p>
      <section class="grid compact-grid">
        ${renderMetric("Selected plan", selectedPlan.label)}
        ${renderMetric("Monthly fee", `$${selectedPlan.monthlyPrice}`)}
        ${renderMetric("Included messages", formatNumber(selectedPlan.includedMessages))}
        ${renderMetric("Billing status", formatBillingStatus(account?.billing_status ?? "not_configured"))}
      </section>
      <ul class="check-list">
        <li>Usage level is customer editable on the profile page.</li>
        <li>Subscription checkout will be attached to Firebase after Square is connected.</li>
        <li>Billing collection fields are intentionally deferred until Square requirements are known.</li>
      </ul>
    </section>
  `;
}

function renderLoadingPanel() {
  return `
    <section class="panel">
      <h3>Loading data</h3>
      <p>Fetching business account state from Firebase.</p>
    </section>
  `;
}

function renderValidationTable(result: CsvValidationResult) {
  return `
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Row</th>
            <th>Phone</th>
            <th>Consent timestamp</th>
            <th>Consent method</th>
            <th>Attested by</th>
            <th>Status</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          ${result.rows
            .map(
              (row) => `
                <tr>
                  <td>${row.row_number}</td>
                  <td>${escapeHtml(row.phone_number)}</td>
                  <td>${escapeHtml(row.consent_received_at)}</td>
                  <td>${escapeHtml(row.consent_method)}</td>
                  <td>${escapeHtml(row.consent_attested_by)}</td>
                  <td>${escapeHtml(row.status)}</td>
                  <td>${escapeHtml(row.rejection_reason ?? "")}</td>
                </tr>
              `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderUploadBatchTable(uploads: UploadRecord[], selectedUploadId: string) {
  return `
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>File</th>
            <th>Status</th>
            <th>Total</th>
            <th>Ready/queued</th>
            <th>Rejected</th>
            <th>Suppressed</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${uploads
            .map(
              (upload) => `
                <tr class="${upload.id === selectedUploadId ? "selected-row" : ""}">
                  <td>${escapeHtml(upload.original_filename)}</td>
                  <td>${escapeHtml(formatUploadStatus(upload.status))}</td>
                  <td>${upload.total_rows}</td>
                  <td>${upload.status === "ready" ? upload.queued_rows : upload.valid_rows}</td>
                  <td>${upload.rejected_rows}</td>
                  <td>${upload.suppressed_rows}</td>
                  <td>${formatDate(upload.created_at)}</td>
                  <td>
                    <div class="table-actions">
                      <button class="secondary-button small-button" type="button" data-select-upload data-upload-id="${escapeHtml(upload.id)}">
                        Review
                      </button>
                      ${
                        upload.status === "validated"
                          ? `<button class="primary-button small-button" type="button" data-start-upload data-upload-id="${escapeHtml(upload.id)}">Start sending</button>`
                          : ""
                      }
                    </div>
                  </td>
                </tr>
              `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderSelectedUploadDetails(details: UploadDetails) {
  const queuedRows = details.rows.filter((row) => row.status === "queued" || row.status === "sent").length;
  const suppressedRows = details.rows.filter((row) => row.status === "rejected_suppressed").length;

  return `
    <section class="panel stack-panel">
      <h3>Batch receipt: ${escapeHtml(details.upload.original_filename)}</h3>
      <section class="grid compact-grid">
        ${renderMetric("Rows", String(details.upload.total_rows))}
        ${renderMetric("Queued/sent", String(queuedRows || details.upload.queued_rows))}
        ${renderMetric("Suppressed", String(suppressedRows || details.upload.suppressed_rows))}
        ${renderMetric("Status", formatUploadStatus(details.upload.status))}
      </section>
      ${details.upload.status === "validated"
        ? `<p class="muted">This batch has been validated but not queued. Click Start sending to apply resend suppression and queue valid rows.</p>`
        : `<p class="muted">This receipt shows which numbers were queued or skipped. Delivery status will update after the SMS worker is connected.</p>`
      }
      ${renderUploadRowsTable(details.rows)}
    </section>
  `;
}

function renderUploadRowsTable(rows: UploadRow[]) {
  return `
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Row</th>
            <th>Phone</th>
            <th>Normalized</th>
            <th>Attested by</th>
            <th>Status</th>
            <th>Queued</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (row) => `
                <tr>
                  <td>${row.row_number}</td>
                  <td>${escapeHtml(row.phone_number)}</td>
                  <td>${escapeHtml(row.normalized_phone_number ?? "")}</td>
                  <td>${escapeHtml(row.consent_attested_by)}</td>
                  <td>${escapeHtml(formatRowStatus(row.status))}</td>
                  <td>${row.queued_at ? formatDate(row.queued_at) : ""}</td>
                  <td>${escapeHtml(row.rejection_reason ?? "")}</td>
                </tr>
              `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderUploadsTable(
  uploads: Array<{
    original_filename: string;
    total_rows: number;
    valid_rows: number;
    rejected_rows: number;
    queued_rows?: number;
    suppressed_rows?: number;
    created_at: string;
  }>,
) {
  return `
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>File</th>
            <th>Total rows</th>
            <th>Valid</th>
            <th>Rejected</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          ${uploads
            .map(
              (upload) => `
                <tr>
                  <td>${escapeHtml(upload.original_filename)}</td>
                  <td>${upload.total_rows}</td>
                  <td>${upload.valid_rows}</td>
                  <td>${upload.rejected_rows}</td>
                  <td>${formatDate(upload.created_at)}</td>
                </tr>
              `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderMetric(label: string, value: string) {
  return `
    <article class="metric-card">
      <div class="metric-label">${label}</div>
      <div class="metric-value">${escapeHtml(value)}</div>
    </article>
  `;
}

function renderUsagePlanOptions(account: BusinessAccount | null | undefined) {
  const selectedPlanId = account?.usage_plan_id ?? usagePlans[0].id;

  return usagePlans
    .map(
      (plan) => `
        <option value="${plan.id}" ${plan.id === selectedPlanId ? "selected" : ""}>
          ${escapeHtml(plan.label)} - $${plan.monthlyPrice} / month for ${formatNumber(plan.includedMessages)} messages
        </option>
      `,
    )
    .join("");
}

function getUsagePlan(planId: UsagePlanId | null | undefined) {
  return usagePlans.find((plan) => plan.id === planId) ?? usagePlans[0];
}

function usagePlanIdFromForm(value: FormDataEntryValue | null): UsagePlanId {
  return value === "growth_1000" || value === "scale_5000" || value === "starter_250"
    ? value
    : usagePlans[0].id;
}

function formatNumber(value: number) {
  return new Intl.NumberFormat("en-US").format(value);
}

function formatBillingStatus(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatUploadStatus(value: UploadRecord["status"]) {
  if (value === "ready") {
    return "Ready / queued";
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatRowStatus(value: UploadRow["status"]) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function nonNegativeIntegerFromForm(value: FormDataEntryValue | null, fallback: number) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
}

function renderBanner(state: { notice: string; error: string }) {
  const items = [
    state.error ? `<div class="banner error">${escapeHtml(state.error)}</div>` : "",
    state.notice ? `<div class="banner notice">${escapeHtml(state.notice)}</div>` : "",
  ].filter(Boolean);

  return items.join("");
}

function disabledAttr(disabled: boolean) {
  return disabled ? "disabled" : "";
}

function readError(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred.";
}

function formatDate(value: string) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
